import React, { useEffect, useRef, forwardRef } from 'react';

const NavAccessibilityWrap = forwardRef((props, ref) => {
    const {className, children, descendantSubMenu = false, enableTabNav = false, ...other} = props;
    const navRef = ref ? ref : useRef();

    const handleKeyDown = (event) => {
        const { key } = event;
        const activeElement = document.activeElement;

        if (!activeElement.classList.contains('ch-nav-item')) {
            return;
        }

        switch (key) {
            case 'Tab':
                if(enableTabNav) {
                    if (event.shiftKey) {
                        const firstItem = getFirstItemAtLevel(activeElement);
                        if (activeElement === firstItem) {
                            return;
                        }

                        event.preventDefault();
                        navigateVertical(activeElement, 'prev');
                    } else {
                        const lastItem = getLastItemAtLevel(activeElement);
                        if (activeElement === lastItem) {
                            return;
                        }

                        event.preventDefault();
                        navigateVertical(activeElement, 'next');
                    }
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                navigateVertical(activeElement, 'next');
                break;
            case 'ArrowUp':
                event.preventDefault();
                navigateVertical(activeElement, 'prev');
                break;
            case 'ArrowRight':
                event.preventDefault();
                const submenu = findSubmenu(activeElement);
                if (submenu) {
                    const firstSubItem = submenu.querySelector('.ch-nav-item');
                    if (firstSubItem) {
                        firstSubItem.tabIndex = 0;
                        firstSubItem.focus();
                    }
                } else {
                    navigateVertical(activeElement, 'next');
                }
                break;
            case 'ArrowLeft':
                event.preventDefault();
                const parentItem = findParentItem(activeElement);
                if (parentItem) {
                    parentItem.focus();
                } else {
                    navigateVertical(activeElement, 'prev');
                }
                break;
            case 'Home':
                event.preventDefault();
                const firstItem = getFirstItemAtLevel(activeElement);
                if (firstItem) {
                    firstItem.focus();
                }
                break;
            case 'End':
                event.preventDefault();
                const lastItem = getLastItemAtLevel(activeElement);
                if (lastItem) {
                    lastItem.focus();
                }
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                activeElement.click();
                const submenuEnter = findSubmenu(activeElement);
                if (submenuEnter) {
                    const firstSubItem = submenuEnter.querySelector('.ch-nav-item, .ant-menu');
                    if (firstSubItem) {
                        firstSubItem.tabIndex = 0;
                        firstSubItem.focus();
                    }
                }
                break;
            default:
                break;
        }
    };

    const navigateVertical = (element, direction) => {
        const parent = findMenuParent(element);
        const items = collectMenuItemsAtCurrentLevel(parent);
        const index = items.indexOf(element);
        if (index === -1) return;

        let newIndex;
        if (direction === 'next') {
            newIndex = (index + 1) % items.length;
        } else if (direction === 'prev') {
            newIndex = (index - 1 + items.length) % items.length;
        }
        items[newIndex].focus();
    };

    const collectMenuItemsAtCurrentLevel = (parent) => {
        const items = [];
        if(descendantSubMenu) {
            for (let child of parent.children) {
                if (child.classList.contains('ch-nav-item')) {
                    items.push(child);
                } else if (child.classList.contains('ch-nav-group')) {
                    // Include items only if the submenu is open
                    const parentItem = findParentItemFromSubmenu(child);
                    if (openSubmenus.has(parentItem)) {
                        items.push(...collectMenuItemsAtCurrentLevel(child));
                    }
                } else {
                    items.push(...collectMenuItemsAtCurrentLevel(child));
                }
            }
        } else {
            for (let child of parent.children) {
                if (child.classList.contains('ch-nav-item')) {
                    items.push(child);
                } else if (child.classList.contains('ch-nav-group')) {
                    // Skip submenus
                    continue;
                } else {
                    // Recursively check for ch-nav-item in child nodes, excluding submenus
                    items.push(...collectMenuItemsAtCurrentLevel(child));
                }
            }
        }

        return items;
    };

    const findSubmenu = (element) => {
        if(descendantSubMenu) {
            return element.querySelector('.ch-nav-group') || null;
        } else {
            let sibling = element.nextElementSibling;
            while (sibling) {
                if (sibling.classList.contains('ch-nav-group')) {
                    return sibling;
                }
                sibling = sibling.nextElementSibling;
            }
        }

        return null;
    };

    const findParentItem = (element) => {
        let parent = element.parentElement;
        if(descendantSubMenu) {
            while (parent && !parent.classList.contains('ch-nav-item') && parent !== navRef.current) {
                parent = parent.parentElement;
            }
            if (parent && parent.classList.contains('ch-nav-item')) {
                return parent;
            }
            return null;
        } else {
            while (parent && !parent.classList.contains('ch-nav-group')) {
                parent = parent.parentElement;
            }
            if (parent) {
                let grandParent = parent.previousElementSibling;
                while (grandParent) {
                    if (grandParent.classList.contains('ch-nav-item')) {
                        return grandParent;
                    }
                    grandParent = grandParent.previousElementSibling;
                }
            }
        }

        return null;
    };

    const findParentItemFromSubmenu = (submenu) => {
        let parent = submenu.parentElement;
        while (parent && !parent.classList.contains('ch-nav-item') && parent !== navRef.current) {
            parent = parent.parentElement;
        }
        if (parent && parent.classList.contains('ch-nav-item')) {
            return parent;
        }
        return null;
    };

    const findAncestorParentItem = (element) => {
        let parent = element.parentElement;
        while (parent && !parent.classList.contains('ch-nav-item') && parent !== navRef.current) {
            parent = parent.parentElement;
        }
        if (parent && parent.classList.contains('ch-nav-item')) {
            return parent;
        }
        return null;
    };

    const findMenuParent = (element) => {
        let parent = element.parentElement;
        while (parent && parent !== navRef.current && !parent.classList.contains('ch-nav-group')) {
            parent = parent.parentElement;
        }
        return parent || navRef.current;
    };

    const getFirstItemAtLevel = (element) => {
        const parent = findMenuParent(element);
        const items = collectMenuItemsAtCurrentLevel(parent);
        return items[0] || null;
    };

    const getLastItemAtLevel = (element) => {
        const parent = findMenuParent(element);
        const items = collectMenuItemsAtCurrentLevel(parent);
        return items[items.length - 1] || null;
    };

    const initializeMenus = () => {
        if (!navRef.current) {
            return; // Exit if the navRef is not set
        }

        // Select all submenu groups within the navigation container
        const subMenus = navRef.current.querySelectorAll('.ch-nav-group');

        subMenus.forEach((subMenu) => {
            // Get all focusable menu items within the current submenu
            const items = subMenu.querySelectorAll('.ch-nav-item');

            // Set tabIndex for each item: first item is focusable, others are not
            items.forEach((item, index) => {
                item.tabIndex = index === 0 ? 0 : -1;
            });
        });
    };


    useEffect(() => {
        //initializeMenus();
    }, [children, navRef]);

    return (
        <nav ref={navRef}
             onKeyDown={handleKeyDown}
             className={(className ? ' ' + className : '')}
             {...other}
        >
            {children}
        </nav>
    );
});

export default NavAccessibilityWrap;
